import { useCivicExpeditionBoard } from './CivicExpeditionProvider';
import { settlementDetailSpriteManifest as art } from './game/asset-manifest';
import { CivicOutcomeArt } from './civic-expedition-art';

export function CivicExpeditionScene({ passive }: { passive: boolean }) {
  const board = useCivicExpeditionBoard();
  if (!board?.enabled || board.error || !board.presentation.current) return null;
  const { stage, restored, current, retainedOutcomes } = board.presentation;
  const familyCode = current?.family_code ?? 'waterways';
  const worksiteVariant =
    current?.outcome?.scene_variant ??
    (familyCode === 'garden_district'
      ? 'garden_landscape'
      : familyCode === 'celestial_observatory'
        ? 'observatory_solar'
        : 'archive_sun');
  return (
    <>
      <div className="civic-city-chronicle" aria-hidden="true">
        {retainedOutcomes.map((entry) => (
          <div
            key={entry.id}
            className="civic-city-outcome"
            data-outcome={entry.outcome!.scene_variant}
          >
            <CivicOutcomeArt variant={entry.outcome!.scene_variant} />
          </div>
        ))}
      </div>
      <div
        aria-hidden="true"
        className="civic-city-worksite"
        data-expedition-stage={stage}
        data-passive={passive || undefined}
        data-family={familyCode}
      >
        <span className="civic-city-worksite__water" />
        {stage !== 'completed' && (
          <img alt="" className="civic-city-worksite__survey" src={art.survey.url} />
        )}
        {restored.wayfinder && (
          <img
            alt=""
            className="civic-city-worksite__canoe"
            src={
              familyCode === 'garden_district'
                ? art.planter.url
                : familyCode === 'celestial_observatory'
                  ? art.survey.url
                  : art.canoe.url
            }
          />
        )}
        {restored.scribe &&
          (['living_archive', 'garden_district', 'celestial_observatory'].includes(familyCode) ? (
            <div className="civic-city-worksite__archive">
              <CivicOutcomeArt variant={worksiteVariant} />
            </div>
          ) : (
            <img alt="" className="civic-city-worksite__banner" src={art.banner.url} />
          ))}
        {restored.builder && (
          <img alt="" className="civic-city-worksite__garden" src={art.planter.url} />
        )}
        {stage === 'completed' && (
          <>
            <img
              alt=""
              className="civic-city-worksite__banner civic-city-worksite__banner--opening"
              src={art.banner.url}
            />
            <img
              alt=""
              className="civic-city-worksite__garden civic-city-worksite__garden--opening"
              src={art.planter.url}
            />
            <span className="civic-city-worksite__opening-light" />
          </>
        )}
      </div>
    </>
  );
}

export function CivicExpeditionCityStatus({ passive }: { passive: boolean }) {
  const board = useCivicExpeditionBoard();
  if (!board) return null;
  const available = board.enabled && !board.isPending && !board.error && board.data;
  const achievement = available ? board.presentation.retainedOutcomes[0]?.outcome : undefined;
  return (
    <aside aria-label="Shared civic expedition" className="civic-city-status">
      <span className="realm-screen__eyebrow">
        Civic Expedition · {board.presentation.familyLabel}
      </span>
      <p aria-live="polite">
        <strong>
          {!board.enabled
            ? 'Expeditions are paused'
            : board.isPending
              ? 'Checking your expedition…'
              : board.error
                ? 'Expedition progress unavailable'
                : board.presentation.title}
        </strong>
        {available && (
          <span>
            {passive
              ? 'Your Tribe’s progress is visible. Actions wait until it is safe to play.'
              : board.presentation.nextAction}
          </span>
        )}
      </p>
      {!passive && board.enabled && (
        <a href="#civic-expedition-title">
          {board.error ? 'Check connection' : 'Review expedition'}
        </a>
      )}
      {achievement && (
        <figure className="civic-city-achievement" data-achievement={achievement.scene_variant}>
          <div className="civic-city-achievement__art" aria-hidden="true">
            <CivicOutcomeArt variant={achievement.scene_variant} />
          </div>
          <figcaption>
            <small>City chronicle · restored expedition</small>
            <strong>{achievement.title}</strong>
            <span>{achievement.description}</span>
            <small>A shared achievement, separate from Great project progress.</small>
          </figcaption>
        </figure>
      )}
    </aside>
  );
}
